// FlairPicker.tsx — Settings → Appearance → Profile flair (1.23.00).
//
// Pro sells "cosmetic profile flair" and the server has stored + rendered it
// since Phase 6, but until now NOTHING could set it: a paying member could only
// change their flair by hand-crafting a PATCH. This is that setter, deliberately
// minimal — four choices, one PATCH, no new plumbing.
//
// GATING. The picker never decides the entitlement; the server does
// (apps/accounts/api_users.py returns 403 `pro_required` for any non-"none"
// flair from a free account). What the client does is show the state honestly:
//   - plan not known yet  → neutral "checking" note, paid options inert
//   - known free          → locked note + upgrade link, paid options disabled
//   - known Pro           → paid options live
// The signal is useBilling() — the real GET /billing/me/ read — NOT the
// `premium` session mirror, which lags webhooks and drifts.
//
// "None" is always available to everyone: clearing is how a lapsed subscriber's
// own client tidies up, and the server allows it for every tier.

import { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Panel, SectionLabel, useToast } from '@/ui';
import { useSession, useBilling } from '@/hooks';
import type { ProfileOverrides } from '@/core/models';

/** The server's UserPreferences.FLAIR choices, verbatim. */
export type FlairKey = 'none' | 'aurora-ring' | 'ember-ring' | 'cyan-ring';

interface FlairOption {
  key: FlairKey;
  label: string;
  /** Theme-token name carrying this ring's per-mode colour (see theme/tokens.ts). */
  token: 'flairAurora' | 'flairEmber' | 'flairCyan' | null;
}

/* Labels are ring-suffixed on purpose: "Aurora" and "Ember" are already the
   aria-labels of two ACCENT swatches in this same Appearance panel, and two
   controls sharing an accessible name is an ambiguity for screen-reader users
   (and for tests). */
const FLAIR_OPTIONS: FlairOption[] = [
  { key: 'none', label: 'No ring', token: null },
  { key: 'aurora-ring', label: 'Aurora ring', token: 'flairAurora' },
  { key: 'ember-ring', label: 'Ember ring', token: 'flairEmber' },
  { key: 'cyan-ring', label: 'Cyan ring', token: 'flairCyan' },
];

/** `ProfileOverrides` carries no `flair` key yet (core/models/account.ts is
 *  outside this change's scope), but the /users/me/ PATCH seam already accepts
 *  and validates one. Intersecting keeps the call type-safe with no cast. */
type FlairPatch = ProfileOverrides & { flair: FlairKey };

const Card = styled(Panel)`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Options = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

type RingToken = 'flairAurora' | 'flairEmber' | 'flairCyan' | null;

const Option = styled.button<{ $ring: RingToken; $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 6px 8px;
  cursor: pointer;
  background: transparent;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.color.textPrimary : theme.color.glassBorder)};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};

  /* The swatch mirrors the real avatar ring (ProfileIdentity's AvatarHalo). */
  &::before {
    content: '';
    width: 18px;
    height: 18px;
    border-radius: 6px;
    background: ${({ theme }) => theme.color.surfaceRaised};
    box-shadow: ${({ $ring, theme }) =>
      $ring ? `0 0 0 3px color-mix(in srgb, ${theme.color[$ring]} 18%, transparent)` : 'none'};
    border: ${({ $ring, theme }) => ($ring ? 'none' : `1px dashed ${theme.color.textFaint}`)};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.accentCyan};
    outline-offset: 2px;
  }
`;

const Note = styled.p`
  margin: -6px 0 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const UpgradeLink = styled(Link)`
  color: ${({ theme }) => theme.color.accentCyan};
  font-weight: 600;
`;

export function FlairPicker() {
  const { currentUser, updateProfile } = useSession();
  const { billing, loaded } = useBilling();
  const toast = useToast();
  const [saving, setSaving] = useState(false);

  const current = (currentUser?.flair as FlairKey | undefined) ?? 'none';
  const planKnown = loaded && billing !== null;
  const isPro = billing?.tier === 'pro';

  async function pick(key: FlairKey) {
    if (saving || key === current) return;
    setSaving(true);
    try {
      const patch: FlairPatch = { flair: key };
      const ok = await updateProfile(patch);
      // updateProfile collapses every failure to `false` — a rejected flair and
      // a dropped connection look the same here, so say what is true of both.
      toast(ok ? 'Profile flair updated' : 'Could not update your flair');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardTitle>Profile flair</CardTitle>
      <SectionLabel>Avatar ring</SectionLabel>

      <Options role="group" aria-label="Profile flair">
        {FLAIR_OPTIONS.map(({ key, label, token }) => {
          // "None" is never gated — clearing works on every tier, server-side too.
          const locked = key !== 'none' && !isPro;
          return (
            <Option
              key={key}
              type="button"
              $ring={token}
              $active={current === key}
              aria-pressed={current === key}
              disabled={locked || saving}
              onClick={() => void pick(key)}
            >
              {label}
            </Option>
          );
        })}
      </Options>

      {!planKnown && <Note>Checking your plan…</Note>}
      {planKnown && !isPro && (
        <Note>
          Profile flair is a Pro feature. <UpgradeLink to="/premium">See Pro</UpgradeLink>.
        </Note>
      )}
      {planKnown && isPro && (
        <Note>
          A coloured ring around your avatar. Cosmetic only: it never affects who sees
          your posts.
        </Note>
      )}
    </Card>
  );
}
